import React, { useState, useCallback, useRef, useLayoutEffect } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MapPin, Star } from "lucide-react";

const destinations = [
  {
    id: 1,
    name: "Maldives",
    subtitle: "Overwater Paradise",
    description: "Experience ultimate luxury in crystal-clear waters",
    image: "https://images.unsplash.com/photo-1697898109604-e06e88b15271?w=1200&q=80",
    rating: 4.9,
    price: "From $2,500/night",
  },
  {
    id: 2,
    name: "Dubai",
    subtitle: "City of Gold",
    description: "Where modern luxury meets timeless elegance",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200&q=80",
    rating: 4.8,
    price: "From $1,800/night",
  },
  {
    id: 3,
    name: "Santorini",
    subtitle: "Aegean Dream",
    description: "Breathtaking sunsets and Mediterranean charm",
    image: "https://images.pexels.com/photos/161342/greece-santorini-architecture-island-161342.jpeg?w=1200",
    rating: 4.9,
    price: "From $1,200/night",
  },
  {
    id: 4,
    name: "Bali",
    subtitle: "Island of Gods",
    description: "Spiritual retreats and tropical luxury",
    image: "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=1200&q=80",
    rating: 4.7,
    price: "From $900/night",
  },
  {
    id: 5,
    name: "Maldives Resort",
    subtitle: "Private Island Escape",
    description: "Exclusive villa experiences over turquoise lagoons",
    image: "https://images.unsplash.com/photo-1601999705946-fbf42c3c6c66?w=1200&q=80",
    rating: 5.0,
    price: "From $3,500/night",
  },
  {
    id: 6,
    name: "Burj Al Arab",
    subtitle: "Seven Star Luxury",
    description: "The world's most luxurious hotel experience",
    image: "https://images.unsplash.com/flagged/photo-1559717201-fbb671ff56b7?w=1200&q=80",
    rating: 4.9,
    price: "From $4,000/night",
  },
];

export const DestinationsCarousel = () => {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const initialized = useRef(false);

  useLayoutEffect(() => {
    if (!api || initialized.current) return;
    
    initialized.current = true;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  return (
    <section
      id="destinations"
      className="py-20 lg:py-32 bg-muted/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-primary text-sm font-semibold tracking-wider uppercase mb-3">
            Explore The World
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Luxury <span className="text-gradient-gold">Destinations</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent mx-auto mb-6" />
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Handpicked exclusive destinations for the discerning traveler
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {destinations.map((destination) => (
                <CarouselItem
                  key={destination.id}
                  className="pl-4 md:basis-1/2 lg:basis-1/3"
                >
                  <div className="group relative h-[500px] rounded-2xl overflow-hidden luxury-card">
                    {/* Image */}
                    <img
                      src={destination.image}
                      alt={destination.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />

                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/30 to-transparent" />

                    {/* Rating Badge */}
                    <div className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1.5 rounded-full bg-background/90 backdrop-blur-sm border border-secondary/30">
                      <Star className="w-4 h-4 text-secondary fill-secondary" />
                      <span className="text-sm font-semibold text-foreground">
                        {destination.rating}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-center gap-2 text-secondary mb-2">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {destination.subtitle}
                        </span>
                      </div>
                      <h3 className="font-display text-2xl font-bold text-background mb-2">
                        {destination.name}
                      </h3>
                      <p className="text-background/80 text-sm mb-4 line-clamp-2">
                        {destination.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-secondary font-semibold">
                          {destination.price}
                        </span>
                        <Button
                          variant="gold"
                          size="sm"
                          className="opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300"
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={scrollPrev}
              className="p-3 rounded-full border border-secondary/40 text-secondary hover:bg-secondary hover:text-background transition-all duration-300 hover:shadow-gold"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {Array.from({ length: count }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => api?.scrollTo(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === current
                      ? "w-8 bg-secondary"
                      : "bg-foreground/30 hover:bg-foreground/50"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={scrollNext}
              className="p-3 rounded-full border border-secondary/40 text-secondary hover:bg-secondary hover:text-background transition-all duration-300 hover:shadow-gold"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DestinationsCarousel;
